/**
 * React Query hooks for WS-3 (fieldiq-api#1286): string events, tallies, the
 * installed string at a well, a tour sheet's pressure-control links and the
 * end-of-job well report.
 *
 * Same contract as useWsJobs: every key starts ['wellServicing'], every write
 * invalidates that prefix (an event moves the job's list, the sheet's string,
 * the well's fold and a draft report's preview at once), and every read
 * spreads LOAD_FAILURE_QUERY_OPTIONS. The /field hooks keep the API's
 * snake_case.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateWs, unwrapWs, unwrapWsList } from './useWellServicing';
import { unwrapWsField } from './useWsJobs';
import { useInvalidatePc } from './usePressureControl';
import type { PcDrillWrite, PcTestWrite } from '../types/pressure-control';
import type {
  WsFieldBopRecorded,
  WsFieldTally,
  WsFieldTourString,
  WsLinkCandidates,
  WsReport,
  WsReportDetail,
  WsReportWrite,
  WsStringEvent,
  WsStringEventWrite,
  WsTally,
  WsTallyLineWrite,
  WsTallyWrite,
  WsTourLinks,
  WsTourString,
  WsWellString,
} from '../types/well-servicing';

export const wsStringKeys = {
  jobEvents: (jobId: string) => ['wellServicing', 'string', 'job', jobId] as const,
  tourString: (tourId: string) => ['wellServicing', 'string', 'tour', tourId] as const,
  wellString: (wellId: string, asOf: string | null) => ['wellServicing', 'string', 'well', wellId, asOf ?? 'all'] as const,
  tallies: (jobId: string) => ['wellServicing', 'tallies', jobId] as const,
  candidates: (tourId: string) => ['wellServicing', 'linkCandidates', tourId] as const,
  reports: (jobId: string) => ['wellServicing', 'reports', jobId] as const,
  report: (id: string) => ['wellServicing', 'report', id] as const,
  fieldTourString: (tourId: string) => ['wellServicing', 'field', 'string', tourId] as const,
};

function useWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWs(qc) });
}

// ─── String events ───────────────────────────────────────────────────────────

export function useWsJobStringEvents(jobId: string | undefined) {
  return useQuery({
    queryKey: wsStringKeys.jobEvents(jobId ?? ''),
    queryFn: async () => unwrapWsList<WsStringEvent>((await api.wellServicing.string.jobEvents(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsTourString(tourId: string | undefined) {
  return useQuery({
    queryKey: wsStringKeys.tourString(tourId ?? ''),
    queryFn: async () => unwrapWs<WsTourString>((await api.wellServicing.string.tour(tourId as string)).data),
    enabled: !!tourId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useAddWsStringEvent = () =>
  useWrite(async ({ tourId, body }: { tourId: string; body: WsStringEventWrite }) =>
    unwrapWs<WsStringEvent>((await api.wellServicing.string.addEvent(tourId, body)).data));
export const useVoidWsStringEvent = () =>
  useWrite(async ({ id, reason }: { id: string; reason: string }) =>
    unwrapWs<WsStringEvent>((await api.wellServicing.string.voidEvent(id, reason)).data));
export const useRemoveWsStringEvent = () => useWrite(async (id: string) => (await api.wellServicing.string.removeEvent(id)).data);

/** The installed string at a well, folded through `asOf` (null = every event). */
export function useWsWellString(wellId: string | undefined, asOf: string | null) {
  return useQuery({
    queryKey: wsStringKeys.wellString(wellId ?? '', asOf),
    queryFn: async () => unwrapWs<WsWellString>((await api.wellServicing.string.well(wellId as string, asOf)).data),
    enabled: !!wellId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── Tallies ─────────────────────────────────────────────────────────────────

export function useWsTallies(jobId: string | undefined) {
  return useQuery({
    queryKey: wsStringKeys.tallies(jobId ?? ''),
    queryFn: async () => unwrapWsList<WsTally>((await api.wellServicing.tallies.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWsTally = () =>
  useWrite(async ({ tourId, body }: { tourId: string; body: WsTallyWrite }) =>
    unwrapWs<WsTally>((await api.wellServicing.tallies.create(tourId, body)).data));
export const useUpdateWsTally = () =>
  useWrite(async ({ id, body }: { id: string; body: WsTallyWrite }) =>
    unwrapWs<WsTally>((await api.wellServicing.tallies.update(id, body)).data));
export const useRemoveWsTally = () => useWrite(async (id: string) => (await api.wellServicing.tallies.remove(id)).data);
export const useVoidWsTally = () =>
  useWrite(async ({ id, reason }: { id: string; reason: string }) =>
    unwrapWs<WsTally>((await api.wellServicing.tallies.void(id, reason)).data));

// ─── A sheet's links to the shared pressure-control records ──────────────────

export function useWsLinkCandidates(tourId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wsStringKeys.candidates(tourId ?? ''),
    queryFn: async () => {
      const { data, meta } = unwrapWs<WsLinkCandidates>((await api.wellServicing.tourLinks.candidates(tourId as string)).data);
      return {
        meta,
        data: {
          pressureTests: Array.isArray(data?.pressureTests) ? data!.pressureTests : [],
          drills: Array.isArray(data?.drills) ? data!.drills : [],
        },
      };
    },
    enabled: !!tourId && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useSetWsTourLinks = () =>
  useWrite(async ({ tourId, kind, ids }: { tourId: string; kind: 'test' | 'drill'; ids: string[] }) =>
    unwrapWs<WsTourLinks>(
      (kind === 'test'
        ? await api.wellServicing.tourLinks.setPressureTests(tourId, ids)
        : await api.wellServicing.tourLinks.setDrills(tourId, ids)
      ).data,
    ));

// ─── Field-recorded BOP tests and drills (fieldiq-api#1324) ──────────────────
// Every write invalidates the ['wellServicing'] prefix, so the field sheet (and
// its pressure_tests / drills) re-reads.

/** A write that moves both the sheet (['wellServicing']) and the shared register (pressure-control keys). */
function useBopWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  const invalidatePc = useInvalidatePc();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void invalidateWs(qc);
      void invalidatePc();
    },
  });
}

/** Warnings ride INSIDE `data` on these (the field() envelope), not beside it. */
export const useRecordWsFieldTourTest = () =>
  useBopWrite(async ({ tourId, body }: { tourId: string; body: PcTestWrite }) =>
    unwrapWsField<WsFieldBopRecorded>((await api.wellServicing.field.recordPressureTest(tourId, body)).data));
export const useRecordWsFieldTourDrill = () =>
  useBopWrite(async ({ tourId, body }: { tourId: string; body: PcDrillWrite }) =>
    unwrapWsField<WsFieldBopRecorded>((await api.wellServicing.field.recordDrill(tourId, body)).data));
export const useRemoveWsFieldTourRecord = () =>
  useBopWrite(async ({ tourId, kind, recordId }: { tourId: string; kind: 'test' | 'drill'; recordId: string }) =>
    (kind === 'test'
      ? await api.wellServicing.field.removePressureTest(tourId, recordId)
      : await api.wellServicing.field.removeDrill(tourId, recordId)).data);

// ─── The well report ─────────────────────────────────────────────────────────

export function useWsReports(jobId: string | undefined) {
  return useQuery({
    queryKey: wsStringKeys.reports(jobId ?? ''),
    queryFn: async () => unwrapWsList<WsReport>((await api.wellServicing.reports.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsReport(id: string | undefined) {
  return useQuery({
    queryKey: wsStringKeys.report(id ?? ''),
    queryFn: async () => unwrapWs<WsReportDetail>((await api.wellServicing.reports.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWsReport = () =>
  useWrite(async ({ jobId, body }: { jobId: string; body: WsReportWrite }) =>
    unwrapWs<WsReportDetail>((await api.wellServicing.reports.create(jobId, body)).data));
export const useUpdateWsReport = () =>
  useWrite(async ({ id, body }: { id: string; body: WsReportWrite }) =>
    unwrapWs<WsReportDetail>((await api.wellServicing.reports.update(id, body)).data));
export const useDeleteWsReport = () => useWrite(async (id: string) => (await api.wellServicing.reports.remove(id)).data);
export const useIssueWsReport = () =>
  useWrite(async (id: string) => unwrapWs<WsReportDetail>((await api.wellServicing.reports.issue(id)).data));

// ─── The field hand (snake_case) ─────────────────────────────────────────────

export function useWsFieldTourString(tourId: string | undefined) {
  return useQuery({
    queryKey: wsStringKeys.fieldTourString(tourId ?? ''),
    queryFn: async () => unwrapWsField<WsFieldTourString>((await api.wellServicing.field.tourString(tourId as string)).data),
    enabled: !!tourId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useAddWsFieldStringEvent = () =>
  useWrite(async ({ tourId, body }: { tourId: string; body: WsStringEventWrite }) =>
    unwrapWsField<WsFieldTourString>((await api.wellServicing.field.addStringEvent(tourId, body)).data));
export const useRemoveWsFieldStringEvent = () =>
  useWrite(async (id: string) => (await api.wellServicing.field.removeStringEvent(id)).data);
export const useCreateWsFieldTally = () =>
  useWrite(async ({ tourId, body }: { tourId: string; body: WsTallyWrite }) =>
    unwrapWsField<WsFieldTally>((await api.wellServicing.field.createTally(tourId, body)).data));
export const useSetWsFieldTallyLines = () =>
  useWrite(async ({ id, lines }: { id: string; lines: WsTallyLineWrite[] }) =>
    unwrapWsField<WsFieldTally>((await api.wellServicing.field.setTallyLines(id, lines)).data));
export const useRemoveWsFieldTally = () => useWrite(async (id: string) => (await api.wellServicing.field.removeTally(id)).data);
